import { diagnosticUuid, mergeDiagnosticEvents } from "./diagnosticsEvents.mjs";
import { createDiagnosticsNavigation } from "./diagnosticsNavigation.mjs";
import { createDiagnosticsPerformance } from "./diagnosticsPerformance.mjs";

export function canUseDiagnostics(user = {}) {
    return user?.is_superuser === true || (user?.groups || []).includes("Amministrazione");
}
export function diagnosticsIdentity(store) {
    const user = store.getters.userdata || {};
    if (!store.getters.isLoggedIn || !canUseDiagnostics(user)) return "";
    const identity = user.id || user.username;
    return identity ? `${identity}:${user.impersonation ? "impersonated" : "direct"}` : "";
}
export function eligibleDiagnosticsUrl(url, base, method = "GET") {
    if (method.toUpperCase() !== "GET") return false;
    try {
        const root = new URL(base);
        const target = new URL(/^(?:[a-z]+:)?\/\//i.test(url) ? url : `${base.replace(/\/$/, "")}/${url.replace(/^\//, "")}`, root);
        const prefix = root.pathname.replace(/\/$/, "") + "/";
        return target.origin === root.origin && target.pathname.startsWith(prefix)
            && !/^(diagnostics|rest-auth|auth|login|logout|token-auth|user-token|impersonate-token|impersonate-login|user-profile)(\/|$)/i.test(target.pathname.slice(prefix.length));
    } catch (_) { return false; }
}

export function createPageDiagnostics({ now: localNow = () => Date.now(), clock = () => performance.now(), uuid = diagnosticUuid, schedule = setTimeout, cancel = clearTimeout, frame = callback => typeof requestAnimationFrame === "undefined" ? callback() : requestAnimationFrame(callback), hidden = () => typeof document !== "undefined" && document.hidden } = {}) {
    let clockOffset = 0;
    const now = () => localNow() + clockOffset;
    const state = { identity: "", session: null, browser: [], events: [], route: "/:unknown", storageError: false, uploadError: false, pendingUploads: 0, droppedEvents: 0, invalidEvents: 0, ready: false, resourcesEnabled: true };
    let storage, credential, storeReference, work, generation = 0, restoring = Promise.resolve(), channel, timer, collector;
    let control = null, inFlight = null, tabId = null, persisted = Promise.resolve(), persistScheduled = false;
    const pending = new Map(), toPersist = new Map();
    const terminal = new Set();
    const seenRevisions = new Map();
    let gcCursor = 0;
    const prefix = identity => `page-diagnostics-v2:${identity}:`;
    const currentTab = () => tabId || (tabId = uuid());
    const nav = createDiagnosticsNavigation({ clock, now, uuid, emit, revise, tabId: currentTab(), schedule, cancel, frame, hidden });
    function refreshBrowser() {
        state.browser = state.events.filter(row => row.kind === "request").map((row, index) => ({ ...row, sequence: index + 1, browser_ms: row.duration_ms ?? null }));
        state.pendingUploads = [...pending.values()].filter(item => item.identity === state.identity).length;
    }
    function persistEvents() {
        persistScheduled = false;
        for (const { key, value } of toPersist.values()) {
            const target = storage;
            persisted = persisted.then(() => target?.setItem(key, value)).catch(() => { state.storageError = true; });
        }
        toPersist.clear();
        return persisted;
    }
    function dirty(event, session = state.session, identity = state.identity) {
        if (!session || !identity) return;
        pending.set(`${session.session_id}:${event.id}`, { event: { ...event }, sessionId: session.session_id, identity, attempts: 0 });
        seenRevisions.set(`${session.session_id}:${event.id}`, event.revision);
        // Revision-specific keys prevent concurrent tabs from overwriting any event.
        toPersist.set(`${session.session_id}:${event.id}`, { key: `${prefix(identity)}event:${session.session_id}:${event.id}:${event.revision}`, value: { event: { ...event }, retain_until: session.retain_until } });
        if (!persistScheduled) { persistScheduled = true; Promise.resolve().then(persistEvents); }
        refreshBrowser();
    }
    function emit(event) {
        if (!state.session?.active || now() >= Date.parse(state.session.expires_at)) return null;
        if (state.events.length >= 1000) { state.droppedEvents++; return null; }
        state.events.push(event); dirty(event); return event;
    }
    function revise(event, values) {
        if (!event || !state.events.some(row => row.id === event.id)) return;
        Object.assign(event, values, { revision: event.revision + 1 }); dirty(event);
    }
    function active() {
        return !!(state.ready && state.session?.active && Date.parse(state.session.expires_at) > now()
            && state.browser.length < (state.session.max_requests || 100));
    }
    function startNavigation(page = state.route, label = "route_enter") {
        state.route = page;
        if (!active()) return;
        nav.enter(page, label); collector?.enter();
    }
    function setRoute(page) {
        // An initially empty $route must not overwrite a template already resolved by a router guard.
        if (page === "/:unknown" && state.route !== "/:unknown") return;
        state.route = page;
        const context = nav.current();
        if (page !== "/:unknown" && context?.event.page === "/:unknown") {
            // Event identity fields are immutable: close the provisional context and create a known one.
            startNavigation(page, context.event.label);
        }
    }
    function compareControl(a, b) { return !b || a.at > b.at || (a.at === b.at && a.id > b.id); }
    function adopt(next, restoringSession = false) {
        if (!next || !compareControl(next, control) || Date.parse(next.session.retain_until) <= localNow() + (next.clock_offset || 0)) return;
        clockOffset = next.clock_offset || 0;
        control = next;
        const changed = next.session.session_id !== state.session?.session_id;
        nav.leave("interrupted"); collector?.stop();
        state.session = { ...(changed ? {} : state.session), ...next.session };
        state.resourcesEnabled = next.resources_enabled !== false;
        if (changed) { state.events = []; state.browser = []; state.droppedEvents = 0; }
        if (active()) { startNavigation(state.route, restoringSession ? "reload" : "route_enter"); if (state.resourcesEnabled) collector?.start(); }
    }
    function publish(session) {
        const next = { id: uuid(), at: Math.max(now(), (control?.at || 0) + 1), clock_offset: clockOffset, session: { session_id: session.session_id, expires_at: session.expires_at, retain_until: session.retain_until, max_requests: session.max_requests || 100, active: session.active }, resources_enabled: state.resourcesEnabled };
        adopt(next);
        if (storage && state.identity) {
            const key = `${prefix(state.identity)}control:${next.at}:${next.id}`;
            persisted = persisted.then(() => storage.setItem(key, next)).catch(() => { state.storageError = true; });
        }
        try { channel?.postMessage({ identity: state.identity, control: next }); } catch (_) { /* Storage fallback remains available. */ }
    }
    async function readStored(identity, version, restore = false) {
        if (!storage || !identity) return;
        const keys = storage.keys ? await storage.keys() : [];
        await cleanupStored(keys, identity);
        const controls = await Promise.all(keys.filter(key => key.startsWith(`${prefix(identity)}control:`)).map(key => storage.getItem(key)));
        if (version !== generation || identity !== state.identity) return;
        const newest = controls.filter(value => value && Date.parse(value.session.retain_until) > localNow() + (value.clock_offset || 0)).sort((a, b) => b.at - a.at || b.id.localeCompare(a.id))[0];
        if (newest) adopt(newest, restore);
        else if (restore) {
            const legacy = await storage.getItem(`page-diagnostics:${identity}`);
            if (version !== generation) return;
            if (legacy?.session && Date.parse(legacy.session.retain_until) > now()) { state.session = { ...legacy.session, active: false }; state.browser = legacy.browser || []; }
        }
        const session = state.session;
        if (!session) return;
        const records = await Promise.all(keys.filter(key => key.startsWith(`${prefix(identity)}event:`)).map(async key => ({ key, value: await storage.getItem(key) })));
        if (version !== generation || session.session_id !== state.session?.session_id) return;
        const latest = new Map();
        for (const record of records) {
            if (!record.value || Date.parse(record.value.retain_until) <= now()) continue;
            const storedSession = record.key.slice(`${prefix(identity)}event:`.length).split(":")[0];
            const event = record.value.event, key = `${storedSession}:${event.id}`;
            if (!latest.has(key) || latest.get(key).event.revision < event.revision) latest.set(key, { event, sessionId: storedSession });
        }
        for (const [key, { event, sessionId }] of latest) {
            if ((seenRevisions.get(key) || 0) >= event.revision) continue;
            seenRevisions.set(key, event.revision);
            if (!terminal.has(`${sessionId}:${event.id}:${event.revision}`)) pending.set(key, { event, sessionId, identity, attempts: 0 });
        }
        state.events = mergeDiagnosticEvents(state.events, [...latest.values()].filter(row => row.sessionId === session.session_id).map(row => row.event));
        if (state.events.length) refreshBrowser();
    }
    async function cleanupStored(keys, identity) {
        if (!storage.removeItem) return;
        const owned = keys.filter(key => key.startsWith(prefix(identity))), latest = new Map();
        for (const key of owned.filter(key => key.includes(":event:"))) {
            const split = key.lastIndexOf(":"), id = key.slice(0, split), revision = Number(key.slice(split + 1));
            if (Number.isFinite(revision)) latest.set(id, Math.max(latest.get(id) || 0, revision));
        }
        // The largest revision key already exists durably. Delete only older keys from this snapshot.
        const obsolete = owned.filter(key => { const split = key.lastIndexOf(":"); return latest.has(key.slice(0, split)) && Number(key.slice(split + 1)) < latest.get(key.slice(0, split)); }).slice(0, 200);
        const sample = owned.slice(gcCursor, gcCursor + 100); gcCursor = gcCursor + 100 >= owned.length ? 0 : gcCursor + 100;
        for (const key of sample) {
            const value = await storage.getItem(key), expires = value?.retain_until || value?.session?.retain_until;
            if (expires && Date.parse(expires) <= now()) obsolete.push(key);
        }
        await Promise.all([...new Set(obsolete)].map(key => storage.removeItem(key)));
    }
    function sync(store, persistence = storage) {
        storage = persistence; storeReference = store;
        const identity = diagnosticsIdentity(store), token = store.getters.token;
        if (identity === state.identity && token === credential && generation) return state.ready ? Promise.resolve() : restoring;
        if (state.identity && state.session?.active) stop();
        const version = ++generation;
        nav.reset(); collector?.stop(); credential = token; control = null;
        state.identity = identity; state.session = null; state.browser = []; state.events = []; state.ready = false;
        state.storageError = false; state.uploadError = false; pending.clear(); terminal.clear(); seenRevisions.clear(); state.pendingUploads = 0;
        restoring = (async () => {
            try { await persisted; await readStored(identity, version, true); }
            catch (_) { if (version === generation) state.storageError = true; }
            if (version !== generation) return;
            state.ready = true;
            if (active() && !nav.current()) { startNavigation(state.route, "reload"); if (state.resourcesEnabled) collector?.start(); }
        })();
        return restoring;
    }
    function calibrate(session) { if (session.server_now && Number.isFinite(Date.parse(session.server_now))) clockOffset = Date.parse(session.server_now) - localNow(); }
    function start(session, resourcesEnabled = true) { calibrate(session); state.resourcesEnabled = resourcesEnabled; publish({ ...session, active: true }); }
    function update(session) {
        if (session.session_id !== state.session?.session_id) return;
        calibrate(session);
        state.session = { ...state.session, ...session, active: state.session.active && session.active };
        state.events = mergeDiagnosticEvents(state.events, session.client_events);
        if (state.events.length) refreshBrowser();
    }
    function stop() {
        if (!state.session) return;
        nav.leave("interrupted"); collector?.stop(); publish({ ...state.session, active: false });
    }
    async function flush(force = false) {
        // Browser persistence is optional: a stalled write must not block upload or stop.
        persistEvents();
        if (inFlight) { await inFlight; if (force) return flush(false); return; }
        if (!work || !state.identity || diagnosticsIdentity(storeReference) !== state.identity || storeReference.getters.token !== credential) return;
        const identity = state.identity, sessions = new Map();
        for (const [key, item] of pending) if (item.identity === identity && (force || item.attempts < 5)) {
            if (!sessions.has(item.sessionId)) sessions.set(item.sessionId, []);
            sessions.get(item.sessionId).push([key, item]);
        }
        inFlight = (async () => {
            for (const [sessionId, candidates] of sessions) for (let offset = 0; offset < candidates.length; offset += 50) {
                const batch = candidates.slice(offset, offset + 50);
                if (identity !== state.identity) return;
                for (const [, item] of batch) item.attempts++;
                try {
                    const response = await work.post(`/diagnostics/sessions/${sessionId}/client/`, { events: batch.map(([, item]) => item.event) }, true, {}, { timeout: 15000 });
                    if (identity !== state.identity) return;
                    const data = response?.data;
                    if (!data?.acknowledged_ids) throw new Error("Missing acknowledgement");
                    for (const [key, item] of batch) {
                        if ((data.acknowledged_ids.includes(item.event.id) || (data.dropped_ids || []).includes(item.event.id)) && pending.get(key)?.event.revision === item.event.revision) pending.delete(key);
                    }
                    state.droppedEvents += (data.dropped_ids || []).length;
                    state.uploadError = (data.retry_ids || []).length > 0;
                } catch (error) {
                    state.uploadError = true;
                    if ([400, 401, 403, 404, 410].includes(error?.response?.status)) {
                        for (const [key, item] of batch) { terminal.add(`${item.sessionId}:${item.event.id}:${item.event.revision}`); if (pending.get(key)?.event.revision === item.event.revision) pending.delete(key); }
                        if (error.response.status === 400) state.invalidEvents += batch.length;
                        else state.droppedEvents += batch.length;
                        continue;
                    }
                    return;
                }
            }
        })();
        try { await inFlight; } finally { inFlight = null; refreshBrowser(); }
    }
    function begin(url, base, store) {
        if (diagnosticsIdentity(store) !== state.identity || store.getters.token !== credential) { sync(store); return null; }
        if (!active() || !eligibleDiagnosticsUrl(url, base)) return null;
        if (!nav.current()) startNavigation();
        else if (state.route !== "/:unknown" && nav.current().event.page !== state.route) startNavigation(state.route, nav.current().event.label);
        const id = uuid(), navigationId = nav.begin(id);
        if (!navigationId) return null;
        const session = { ...state.session }, identity = state.identity, started = clock(), version = generation;
        const row = emit({ id, tab_id: currentTab(), navigation_id: navigationId, revision: 1, kind: "request", label: "api", page: state.route, started_at: new Date(now()).toISOString(), outcome: "in_progress", visibility_hidden: hidden() });
        if (!row) { nav.finish(navigationId, id); return null; }
        let finished = false;
        return { sessionId: session.session_id, clientId: id, navigationId, finish(response, error) {
            if (finished || identity !== state.identity || version !== generation) return;
            finished = true;
            const values = { duration_ms: Math.max(0, Math.round((clock() - started) * 10) / 10), ended_at: new Date(now()).toISOString(), status_code: response?.status || null, request_id: response?.headers?.["x-work-diagnostics-request"] || null, outcome: error ? error.code === "ERR_CANCELED" ? "cancelled" : ["ECONNABORTED", "ETIMEDOUT"].includes(error.code) ? "timeout" : "error" : "completed", visibility_hidden: row.visibility_hidden || hidden() };
            if (state.session?.session_id === session.session_id) { revise(row, values); nav.finish(navigationId, id); }
            else { Object.assign(row, values, { revision: row.revision + 1 }); dirty(row, session, identity); }
            if (!active()) flush();
        } };
    }
    function receive(message) { if (message?.identity === state.identity && state.ready) adopt(message.control); }
    async function tick() {
        try {
            if (!state.ready || !state.identity) return;
            await readStored(state.identity, generation);
            if (!active()) { nav.leave("interrupted"); collector?.stop(); }
            if (pending.size) await flush();
        } catch (_) { state.storageError = true; }
    }
    function connect({ api, store, persistence, browser = typeof window !== "undefined" ? window : null }) {
        work = api; sync(store, persistence);
        if (timer || !browser) return;
        collector = createDiagnosticsPerformance({ navigation: nav, emit, revise, uuid, tabId: currentTab(), now, performanceObject: browser.performance, Observer: browser.PerformanceObserver, pageOrigin: browser.location.origin, apiOrigin: new URL(api.url).origin });
        try { if (browser.BroadcastChannel) { channel = new browser.BroadcastChannel("work-page-diagnostics-v2"); channel.onmessage = message => receive(message.data); } } catch (_) { /* Periodic storage sync is the fallback. */ }
        timer = browser.setInterval(tick, 2500);
        browser.document.addEventListener("visibilitychange", nav.visibility);
        browser.addEventListener("pagehide", onPageHide); browser.addEventListener("load", onLoad);
        restoring.then(() => { if (active() && state.resourcesEnabled) collector.start(); });
    }
    function onLoad() { try { collector?.documentTiming(); nav.milestone("load"); } catch (_) { /* Optional measurement. */ } }
    function onPageHide() { try { nav.milestone("pagehide"); nav.leave("interrupted"); flush(); } catch (_) { /* Persisted events survive interrupted upload. */ } }
    function milestone(label) { try { if (active()) nav.milestone(label); } catch (_) { /* Never change an application loader. */ } }
    return { state, active, sync, start, update, stop, begin, flush, tick, connect, receive, startNavigation, setRoute, milestone, navigation: nav, currentTime: now, clockOffset: () => clockOffset, persisted: () => persistEvents() };
}
export const pageDiagnostics = createPageDiagnostics();
